
import {Vue, Component, Prop, Watch} from 'vue-property-decorator';
import AmountDisplayerFilterable from '@/components/reports-v3/components/filterables/AmountDisplayerFilterable.vue';
import {COL, sqlSum, sqlSumByDocType, getTableName} from '@/worker/db/sqlHelpers';
import FilterWidget from '../shared/FilterWidget.vue';
import {
    CustomerSupplierTradeCard,
    PermissionsGroup,
    SalesDetailCard,
} from '@/store/models.def';
import moment from 'moment';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
    components: {
        AmountDisplayerFilterable,
        IncludeCreditNoteToggle,
        IncludeDebitNoteToggle,
        SlPhModuleOnlyToggle,
    },
})
export default class Sales12MonthsSalesNumberDisplayer extends FilterWidget {
    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
        | 'docproject'
        | 'dtlproject'
    > = [];

    public salesAmount = 0;

    public customersCount = 0;
    public activeCustomersCount = 0;
    public servingYear = '' || 0;

    public get oneYearBeforeRange(): [number, number] {
        return [
            moment(this.selectedAsOfDate).add(-1, 'year').add(1, 'month').startOf('month').valueOf(),
            moment(this.selectedAsOfDate).endOf('month').valueOf(),
        ];
    }

    public get includeCreditNote() {
        return settingx.now.includeCreditNote;
    }

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

    public get slPhModuleOnly() {
        return settingx.now.slPhModuleOnly;
    }

    public get permissionIds(): PermissionsGroup[] {
        return ['customers', 'sales'];
    }

    public get expensiveHook() {
        const {
            selectedDateRange,
            selectedAsOfDate,
            selectedStockItems,
            selectedDocProjects,
            selectedDTLProjects,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        } = this;
        return JSON.stringify([
            selectedDateRange,
            selectedAsOfDate,
            selectedStockItems,
            selectedDocProjects,
            selectedDTLProjects,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        ]);
    }

    public async expensiveCalc() {
        const jobId = this.currentJobId;
        this.generateLoadingText(0.2, 0.3);

        // Build filter clauses manually for cross-table UNION
        const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
        const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.oneYearBeforeRange[0]} AND ${this.oneYearBeforeRange[1]}`;

        let docProjectFilter = '';
        if (this.selectedDocProjects.length > 0) {
            const list = this.selectedDocProjects.map((p) => `'${escapeStr(p)}'`).join(',');
            docProjectFilter = ` AND "DOCPROJECT" IN (${list})`;
        }

        let dtlProjectFilter = '';
        if (this.selectedDTLProjects.length > 0) {
            const list = this.selectedDTLProjects.map((p) => `'${escapeStr(p)}'`).join(',');
            dtlProjectFilter = ` AND "DTLPROJECT" IN (${list})`;
        }

        const baseFilter = `${dateFilter}${docProjectFilter}${dtlProjectFilter}`;

        // Get table names
        const salesTable = getTableName('sales');
        const cnTable = getTableName('creditNotes');
        const arTable = getTableName('customerTrades');
        const globalCustomersTable = getTableName('globalCustomers');

        // Get a head for executing queries (no filters needed)
        // Ensure all tables are loaded before querying
        const queryHead = await this._ensureTablesLoaded([
            'sales', 'creditNotes', 'customerTrades', 'globalCustomers',
        ]);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.3, 0.9);

        // Run all queries in parallel
        const [slResult, arResult, globalResult] = await Promise.all([
            // Single UNION query for SL data (sales + creditNotes)
            queryHead.rawQuery(`
                SELECT
                    SUM(CASE WHEN src = 'sales' AND docType IN ('IV', 'CS') THEN amount ELSE 0 END) as iv_cs_amount,
                    SUM(CASE WHEN src = 'sales' AND docType = 'DN' THEN amount ELSE 0 END) as dn_amount,
                    SUM(CASE WHEN src = 'cn' THEN amount ELSE 0 END) as cn_amount,
                    COUNT(DISTINCT code) as customer_count
                FROM (
                    SELECT 'sales' as src, ${COL.DOCTYPE} as docType,
                        TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount, ${COL.CODE} as code
                    FROM ${salesTable}
                    WHERE ${baseFilter}
                    UNION ALL
                    SELECT 'cn' as src, 'CN' as docType,
                        TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount, ${COL.CODE} as code
                    FROM ${cnTable}
                    WHERE ${baseFilter}
                ) AS combined
            `),
            // Single query for AR data
            queryHead.rawQuery(`
                SELECT
                    ${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amount')},
                    ${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')},
                    ${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amount')},
                    COUNT(DISTINCT ${COL.CODE}) as customer_count
                FROM ${arTable}
                WHERE ${baseFilter}
            `),
            // Global customers count (where firstDeal is not null and <= asOfDate)
            queryHead.rawQuery(`
                SELECT COUNT(*) as cnt
                FROM ${globalCustomersTable}
                WHERE TRY_CAST("FIRSTDEAL" AS BIGINT) IS NOT NULL
                    AND TRY_CAST("FIRSTDEAL" AS BIGINT) <= ${this.selectedAsOfDate}
            `),
        ]);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.95);

        // Extract values
        const slRow = slResult[0] || {};
        const slIvCsAmount = Number(slRow.iv_cs_amount) || 0;
        const slDnAmount = Number(slRow.dn_amount) || 0;
        const slCnAmount = Number(slRow.cn_amount) || 0;
        const slCustomerCount = Number(slRow.customer_count) || 0;

        const arRow = arResult[0] || {};
        const arIvAmount = Number(arRow.iv_amount) || 0;
        const arCnAmount = Number(arRow.cn_amount) || 0;
        const arDnAmount = Number(arRow.dn_amount) || 0;
        const arCustomerCount = Number(arRow.customer_count) || 0;

        this.customersCount = Number((globalResult[0] || {}).cnt) || 0;

        let salesAmount: number;
        let activeCustomersCount: number;

        if (this.slPhModuleOnly) {
            // SL Module calculations
            salesAmount = slIvCsAmount
                - (this.includeCreditNote ? slCnAmount : 0)
                + (this.includeDebitNote ? slDnAmount : 0);

            activeCustomersCount = slCustomerCount;
        } else {
            // AR Module calculations
            salesAmount = arIvAmount
                - (this.includeCreditNote ? arCnAmount : 0)
                + (this.includeDebitNote ? arDnAmount : 0);

            activeCustomersCount = arCustomerCount;
        }

        this.activeCustomersCount = activeCustomersCount;
        this.salesAmount = salesAmount;
        this.servingYear = 0;

        this.generateLoadingText(1);
        this.saveHistory('salesAmount', 'servingYear', 'activeCustomersCount');
    }
}
